package com.rainbowclubs.rainbow.presentation.home

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.rainbowclubs.rainbow.data.models.AttendeeStatus
import com.rainbowclubs.rainbow.data.models.HomeResponse
import com.rainbowclubs.rainbow.data.requests.UpdateEventAttendeeRequest
import com.rainbowclubs.rainbow.domain.use_cases.events.UpdateEventAttendeeUseCase
import com.rainbowclubs.rainbow.domain.use_cases.home.GetHomeDataUseCase
import com.rainbowclubs.rainbow.presentation.ScreenState
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.launch
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Instant

class HomeViewModel(
    private val clubId: String,
    private val getHomeDataUseCase: GetHomeDataUseCase,
    private val updateEventAttendeeUseCase: UpdateEventAttendeeUseCase,
) : ViewModel() {

    sealed class HomeNavigationEvent {
        data class Admin(val clubId: String) : HomeNavigationEvent()
        data class Club(val clubId: String) : HomeNavigationEvent()
        data class Messages(val clubId: String) : HomeNavigationEvent()
        data class More(val clubId: String) : HomeNavigationEvent()
        data class EventDetails(val clubId: String, val eventId: String) : HomeNavigationEvent()
    }

    private val _screenState = MutableStateFlow<ScreenState<HomeResponse>>(ScreenState.Loading)
    val screenState: StateFlow<ScreenState<HomeResponse>> = _screenState.asStateFlow()

    private val _rsvpLoadingState = MutableStateFlow<Pair<String, AttendeeStatus>?>(null)
    val rsvpLoadingState: StateFlow<Pair<String, AttendeeStatus>?> = _rsvpLoadingState.asStateFlow()

    private val navigationIntentChannel = Channel<HomeNavigationEvent>(Channel.BUFFERED)
    val navigationIntent: Flow<HomeNavigationEvent> = navigationIntentChannel.receiveAsFlow()

    init {
        fetchHomeData()
    }

    fun fetchHomeData() {
        viewModelScope.launch {
            _screenState.value = ScreenState.Loading
            getHomeDataUseCase(clubId)
                .onSuccess {
                    _screenState.value = ScreenState.Content(it)
                }
                .onFailure {
                    _screenState.value = ScreenState.Error(it)
                }
        }
    }

    fun updateRsvp(status: AttendeeStatus, eventId: String) {
        viewModelScope.launch {
            _rsvpLoadingState.value = eventId to status
            val request = UpdateEventAttendeeRequest(eventId, status)
            updateEventAttendeeUseCase(request)
                .onSuccess { updatedEvent ->
                    val currentState = _screenState.value
                    if (currentState is ScreenState.Content) {
                        val data = currentState.data
                        val updatedData = if (data.ongoingEvent?.id == updatedEvent.id) {
                            data.copy(ongoingEvent = updatedEvent)
                        } else if (data.upcomingEvent?.id == updatedEvent.id) {
                            data.copy(upcomingEvent = updatedEvent)
                        } else {
                            data
                        }
                        _screenState.value = ScreenState.Content(updatedData)
                    }
                }
                .onFailure {
                    // Handle failure
                }
            _rsvpLoadingState.value = null
        }
    }

    fun navigateToClub() {
        navigationIntentChannel.trySend(HomeNavigationEvent.Club(clubId))
    }

    fun navigateToAdmin() {
        navigationIntentChannel.trySend(HomeNavigationEvent.Admin(clubId))
    }

    fun navigateToMessages() {
        navigationIntentChannel.trySend(HomeNavigationEvent.Messages(clubId))
    }

    fun navigateToMore() {
        navigationIntentChannel.trySend(HomeNavigationEvent.More(clubId))
    }

    fun navigateToEventDetails(eventId: String) {
        navigationIntentChannel.trySend(HomeNavigationEvent.EventDetails(clubId, eventId))
    }

    fun formatDate(timestamp: Long): String {
        val instant = Instant.fromEpochMilliseconds(timestamp)
        val dateTime = instant.toLocalDateTime(TimeZone.currentSystemDefault())

        val dayOfWeek =
            dateTime.dayOfWeek.name.lowercase().replaceFirstChar { it.uppercase() }.take(3)
        val month = dateTime.month.name.lowercase().replaceFirstChar { it.uppercase() }.take(3)
        val day = dateTime.day.toString().padStart(2, '0')
        val year = dateTime.year

        return "$dayOfWeek, $month $day $year"
    }

    fun formatTime(timestamp: Long): String {
        val instant = Instant.fromEpochMilliseconds(timestamp)
        val dateTime = instant.toLocalDateTime(TimeZone.currentSystemDefault())

        val hour = if (dateTime.hour % 12 == 0) 12 else dateTime.hour % 12
        val minute = dateTime.minute.toString().padStart(2, '0')
        val amPm = if (dateTime.hour < 12) "AM" else "PM"

        return "$hour:$minute $amPm"
    }
}
